import axios from 'axios'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {useSelector} from 'react-redux'

import SearchIcon from '@mui/icons-material/Search'
import {
  Box,
  Button,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import {DataGridPro, GridColDef} from '@mui/x-data-grid-pro'
import Swal from 'sweetalert2'

import {chargeBackHost} from 'GLOBAL/envVariables'
import MeterReaderFormDialog, {
  MeterReaderFormErrorsType,
  MeterReaderFormType,
} from './MeterReaderFormDialog'

type RowType = {
  id: number
  office: string
  name: string
  code: string
  active: boolean
}

type StatusFilterType = 'all' | 'active' | 'inactive'

const MeterReaderManagement = () => {
  const officeValue = useSelector((state: any) => state?.officeInfo.value)

  const [rows, setRows] = useState<RowType[]>([])
  const [searchText, setSearchText] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilterType>('all')

  const [form, setForm] = useState<MeterReaderFormType>({
    office: '',
    name: '',
    code: '',
    active: true,
  })

  const [formErrors, setFormErrors] = useState<MeterReaderFormErrorsType>({
    name: '',
    code: '',
  })

  const [openFormModal, setOpenFormModal] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [selectedRow, setSelectedRow] = useState<RowType | null>(null)

  const resetForm = useCallback(() => {
    setForm({
      office: officeValue || '',
      name: '',
      code: '',
      active: true,
    })
    setFormErrors({
      name: '',
      code: '',
    })
    setSelectedRow(null)
    setIsEditMode(false)
  }, [officeValue])

  const fetchMeterReaders = useCallback(async () => {
    try {
      const res = await axios.get(`${chargeBackHost}/api/gumdam/select?office_id=${officeValue}`)

      if (res.data.success) {
        const mapped = res.data.data.map((item: any, idx: number) => ({
          id: idx + 1,
          office: item.office_id,
          name: item.gum_name,
          code: item.gum_code,
          active: item.used_status,
        }))

        setRows(mapped)
      }
    } catch (err) {
      console.error(err)
      Swal.fire({
        icon: 'error',
        title: '조회 실패',
        text: '검침원 목록을 불러오지 못했습니다.',
        confirmButtonText: '확인',
        confirmButtonColor: '#d32f2f',
      })
    }
  }, [officeValue])

  useEffect(() => {
    fetchMeterReaders()
  }, [fetchMeterReaders])

  const validateForm = () => {
    const trimmedName = (form.name ?? '').trim()
    const trimmedCode = (form.code ?? '').trim()

    const nextErrors: MeterReaderFormErrorsType = {
      name: '',
      code: '',
    }

    if (!trimmedName) {
      nextErrors.name = '검침원성명을 입력해주세요.'
    }

    if (!trimmedCode) {
      nextErrors.code = '검침원코드를 입력해주세요.'
    }

    setFormErrors(nextErrors)

    return !nextErrors.name && !nextErrors.code
  }

  const handleFormChange = (field: keyof MeterReaderFormType, value: string | boolean) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }))

    if (field === 'name' || field === 'code') {
      setFormErrors((prev) => ({
        ...prev,
        [field]: '',
      }))
    }
  }

  const handleOpenAddModal = () => {
    setIsEditMode(false)
    setSelectedRow(null)
    setForm({
      office: officeValue || '',
      name: '',
      code: '',
      active: true,
    })
    setFormErrors({
      name: '',
      code: '',
    })
    setOpenFormModal(true)
  }

  const handleOpenEditModal = (row: RowType) => {
    setIsEditMode(true)
    setSelectedRow(row)
    setForm({
      office: row.office,
      name: row.name,
      code: row.code,
      active: row.active,
    })
    setFormErrors({
      name: '',
      code: '',
    })
    setOpenFormModal(true)
  }

  const handleCloseFormModal = () => {
    setOpenFormModal(false)
    resetForm()
  }

  const toggleStatus = async (row: RowType) => {
    const newStatus = !row.active

    try {
      await axios.patch(
        `${chargeBackHost}/api/gumdam/update-status`,
        {},
        {
          params: {
            office_id: row.office,
            gum_code: row.code,
            used_status: newStatus,
          },
        }
      )

      setRows((prev) => prev.map((r) => (r.code === row.code ? {...r, active: newStatus} : r)))
    } catch (err) {
      console.error(err)
      Swal.fire({
        icon: 'error',
        title: '변경 실패',
        text: '계정 상태를 변경하지 못했습니다.',
        confirmButtonText: '확인',
        confirmButtonColor: '#d32f2f',
      })
    }
  }

  const saveNewRow = async () => {
    const isValid = validateForm()
    if (!isValid) return

    const trimmedName = (form.name ?? '').trim()
    const trimmedCode = (form.code ?? '').trim()

    try {
      await axios.post(`${chargeBackHost}/api/gumdam/create`, {
        office_id: form.office,
        gum_name: trimmedName,
        gum_code: trimmedCode,
        used_status: form.active,
      })

      handleCloseFormModal()
      await fetchMeterReaders()

      Swal.fire({
        icon: 'success',
        title: '저장 완료',
        text: '검침원이 추가되었습니다.',
        confirmButtonText: '확인',
        confirmButtonColor: '#1976d2',
      })
    } catch (err: any) {
      console.error(err)

      const errorMessage = err?.response?.data?.response || '검침원 추가 중 오류가 발생했습니다.'

      handleCloseFormModal()

      Swal.fire({
        icon: 'error',
        title: '저장 실패',
        text: errorMessage,
        confirmButtonText: '확인',
        confirmButtonColor: '#d32f2f',
      })
    }
  }

  const updateRow = async () => {
    const isValid = validateForm()
    if (!isValid || !selectedRow) return

    const trimmedName = (form.name ?? '').trim()
    const trimmedCode = (form.code ?? '').trim()

    try {
      await axios.post(`${chargeBackHost}/api/gumdam/update-info`, {
        office_id: selectedRow.office,
        gum_code: selectedRow.code,
        new_gum_name: trimmedName,
        new_gum_code: trimmedCode,
        used_status: form.active,
      })

      handleCloseFormModal()
      await fetchMeterReaders()

      Swal.fire({
        icon: 'success',
        title: '수정 완료',
        text: '검침원 정보가 수정되었습니다.',
        confirmButtonText: '확인',
        confirmButtonColor: '#1976d2',
      })
    } catch (err: any) {
      console.error(err)

      const errorMessage = err?.response?.data?.response || '검침원 수정 중 오류가 발생했습니다.'

      handleCloseFormModal()

      Swal.fire({
        icon: 'error',
        title: '수정 실패',
        text: errorMessage,
        confirmButtonText: '확인',
        confirmButtonColor: '#d32f2f',
      })
    }
  }

  const handleSubmit = async () => {
    if (isEditMode) {
      await updateRow()
      return
    }

    await saveNewRow()
  }

  const filteredRows = useMemo(() => {
    const keyword = searchText.trim().toLowerCase()

    return rows.filter((row) => {
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && row.active) ||
        (statusFilter === 'inactive' && !row.active)

      if (!matchesStatus) return false

      if (!keyword) return true

      const office = String(row.office ?? '').toLowerCase()
      const name = String(row.name ?? '').toLowerCase()
      const code = String(row.code ?? '').toLowerCase()
      const activeText = row.active ? '사용' : '미사용'

      return (
        office.includes(keyword) ||
        name.includes(keyword) ||
        code.includes(keyword) ||
        activeText.includes(keyword)
      )
    })
  }, [rows, searchText, statusFilter])

  const columns: GridColDef[] = [
    {
      field: 'office',
      headerName: '사업소',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'name',
      headerName: '검침원이름',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'code',
      headerName: '검침원코드',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'active',
      headerName: '계정 상태',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: (params) => (
        <Switch
          checked={params.row.active}
          onClick={(e) => e.stopPropagation()}
          onChange={() => toggleStatus(params.row)}
          color='success'
        />
      ),
    },
  ]

  return (
    <Box display='flex' flexDirection='column' minWidth={0} height='100%'>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography variant='h6'>검침원 관리</Typography>
          <Typography
            variant='body2'
            sx={{
              fontSize: '14px',
              fontWeight: '500',
              color: 'text.secondary',
            }}
          >
            요금연계에 등록된 검침 담당자를 등록 및 계정 상태를 관리합니다.
          </Typography>
        </Box>

        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
          <FormControl size='small' sx={{minWidth: 140}}>
            <InputLabel id='status-filter-label'>계정상태</InputLabel>
            <Select
              labelId='status-filter-label'
              value={statusFilter}
              label='계정상태'
              onChange={(e) => setStatusFilter(e.target.value as StatusFilterType)}
            >
              <MenuItem value='all'>전체</MenuItem>
              <MenuItem value='active'>사용</MenuItem>
              <MenuItem value='inactive'>미사용</MenuItem>
            </Select>
          </FormControl>

          <TextField
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder='검색'
            size='small'
            sx={{width: 280}}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon fontSize='small' />
                </InputAdornment>
              ),
            }}
          />

          <Button
            variant='contained'
            onClick={handleOpenAddModal}
            sx={{height: 48, minWidth: 120, whiteSpace: 'nowrap'}}
          >
            검침원 등록
          </Button>
        </Box>
      </Box>

      <Box sx={{flex: 1, minHeight: 0}}>
        <DataGridPro
          rows={filteredRows}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 20, 50]}
          disableSelectionOnClick
          onRowClick={(params) => handleOpenEditModal(params.row)}
          initialState={{
            sorting: {
              sortModel: [{field: 'name', sort: 'asc'}],
            },
          }}
          sx={{
            '& .MuiDataGrid-root, & .MuiDataGrid-columnHeaders': {
              fontFamily: 'Noto Sans KR, sans-serif',
            },
            '& .MuiDataGrid-columnHeaders': {backgroundColor: '#f3f4f6'},
            '& .MuiDataGrid-cell': {fontSize: '16px', fontWeight: '400'},
            '& .MuiDataGrid-pinnedColumns': {backgroundColor: '#f9fafb'},
            '& .MuiDataGrid-pinnedColumnHeaders': {backgroundColor: '#f3f4f6'},
            '& .edited-row': {backgroundColor: '#e9f7ef'},
            '& .MuiDataGrid-row.warning-row': {backgroundColor: '#fef9e7'},
            '& .MuiDataGrid-cell:focus': {outline: 'none'},
            '& .MuiDataGrid-cell:focus-within': {outline: 'none'},
          }}
        />
      </Box>

      <MeterReaderFormDialog
        open={openFormModal}
        isEditMode={isEditMode}
        form={form}
        errors={formErrors}
        onClose={handleCloseFormModal}
        onChange={handleFormChange}
        onSubmit={handleSubmit}
      />
    </Box>
  )
}

export default MeterReaderManagement
